import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Chip,
  LinearProgress,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  AccountBalance as BankIcon,
  Add as AddIcon,
  Sync as SyncIcon,
  CheckCircle as ConnectedIcon,
  Error as DisconnectedIcon,
  Visibility as ViewIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  CloudSync as OpenBankingIcon,
  TrendingUp as TransactionIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

import BankAccountList from './BankAccountList';
import TransactionImport from './TransactionImport';
import BankReconciliation from './BankReconciliation';
import OpenBankingSetup from './OpenBankingSetup';

// Types
interface BankAccount {
  id: string;
  bank_name: string;
  account_name: string;
  account_number: string;
  sort_code: string;
  account_type: 'CURRENT' | 'SAVINGS' | 'BUSINESS';
  balance: number;
  balance_formatted: string;
  currency: string;
  status: 'CONNECTED' | 'DISCONNECTED' | 'PENDING';
  last_sync: string;
  provider: string;
  logo_url?: string;
}

interface OpenBankingStatus {
  is_enabled: boolean;
  connected_banks: number;
  total_accounts: number;
  last_sync: string;
  sync_status: 'SUCCESS' | 'FAILED' | 'IN_PROGRESS';
  compliance_status: 'COMPLIANT' | 'EXPIRED' | 'PENDING';
}

const BankingIntegration: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [openBankingStatus, setOpenBankingStatus] = useState<OpenBankingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [connectingBank, setConnectingBank] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const bankingTabs = [
    {
      label: 'Bank Accounts',
      icon: <BankIcon />,
      component: BankAccountList,
      description: 'Manage connected bank accounts and view balances'
    },
    {
      label: 'Transaction Import',
      icon: <TransactionIcon />,
      component: TransactionImport,
      description: 'Import and categorize bank transactions automatically'
    },
    {
      label: 'Reconciliation',
      icon: <SyncIcon />,
      component: BankReconciliation,
      description: 'Match imported transactions with accounting records'
    },
    {
      label: 'Open Banking Setup',
      icon: <OpenBankingIcon />,
      component: OpenBankingSetup,
      description: 'Configure Open Banking connections and permissions'
    }
  ];

  // Major UK Banks for Open Banking
  const ukBanks = [
    { name: 'Barclays', logo: '🏦', status: 'available' },
    { name: 'HSBC', logo: '🏦', status: 'available' },
    { name: 'Lloyds Bank', logo: '🏦', status: 'available' },
    { name: 'NatWest', logo: '🏦', status: 'available' },
    { name: 'Santander UK', logo: '🏦', status: 'available' },
    { name: 'TSB', logo: '🏦', status: 'available' },
    { name: 'Nationwide', logo: '🏦', status: 'available' },
    { name: 'Halifax', logo: '🏦', status: 'available' },
    { name: 'First Direct', logo: '🏦', status: 'available' },
    { name: 'Metro Bank', logo: '🏦', status: 'available' },
    { name: 'Monzo', logo: '🏦', status: 'available' },
    { name: 'Starling Bank', logo: '🏦', status: 'available' }
  ];

  useEffect(() => {
    fetchBankingData();
  }, []);

  const fetchBankingData = async () => {
    try {
      setLoading(true);

      // Fetch bank accounts and Open Banking status
      const [accountsResponse, statusResponse] = await Promise.all([
        fetch('/api/v1/banking/accounts'),
        fetch('/api/v1/banking/open-banking-status')
      ]);

      if (accountsResponse.ok) {
        const accountsData = await accountsResponse.json();
        setBankAccounts(accountsData.accounts || []);
      }

      if (statusResponse.ok) {
        const statusData = await statusResponse.json();
        setOpenBankingStatus(statusData);
      }
    } catch (error) {
      console.error('Error fetching banking data:', error);
      setSnackbar({ open: true, message: 'Failed to load banking data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSyncAllAccounts = async () => {
    try {
      setSyncing(true);

      const response = await fetch('/api/v1/banking/sync-all', {
        method: 'POST'
      });

      if (response.ok) {
        setSnackbar({ open: true, message: 'Bank accounts synced successfully!', severity: 'success' });
        fetchBankingData();
      } else {
        throw new Error('Failed to sync accounts');
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to sync bank accounts', severity: 'error' });
    } finally {
      setSyncing(false);
    }
  };

  const handleConnectBank = async (bankName: string) => {
    try {
      setConnectingBank(bankName);

      const response = await fetch(`/api/v1/banking/connect-bank?bank_name=${encodeURIComponent(bankName)}&account_type=BUSINESS`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSnackbar({
          open: true,
          message: `Successfully initiated connection to ${bankName}. ${data.next_step}`,
          severity: 'success'
        });
        setShowSetupDialog(false);

        // Refresh banking data to show the new pending connection
        setTimeout(() => {
          fetchBankingData();
        }, 1000);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to connect bank');
      }
    } catch (error) {
      console.error('Error connecting bank:', error);
      setSnackbar({
        open: true,
        message: `Failed to connect to ${bankName}. Please try again.`,
        severity: 'error'
      });
    } finally {
      setConnectingBank(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'CONNECTED': return 'success';
      case 'DISCONNECTED': return 'error';
      case 'PENDING': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'CONNECTED': return <ConnectedIcon />;
      case 'DISCONNECTED': return <DisconnectedIcon />;
      case 'PENDING': return <WarningIcon />;
      default: return <BankIcon />;
    }
  };

  const ActiveTabComponent = bankingTabs[activeTab].component;

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Banking Integration</Typography>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Banking Integration
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Open Banking connectivity with major UK banks
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setShowSetupDialog(true)}
          >
            Connect Bank
          </Button>
          <Button
            variant="contained"
            startIcon={<SyncIcon />}
            onClick={handleSyncAllAccounts}
            disabled={syncing}
          >
            {syncing ? 'Syncing...' : 'Sync All'}
          </Button>
        </Box>
      </Box>

      {/* Open Banking Status */}
      {openBankingStatus && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={3}>
                <Box display="flex" alignItems="center" gap={1}>
                  <OpenBankingIcon color="primary" />
                  <Box>
                    <Typography variant="h6">Open Banking Status</Typography>
                    <Chip
                      label={openBankingStatus.compliance_status}
                      color={openBankingStatus.compliance_status === 'COMPLIANT' ? 'success' : 'warning'}
                      size="small"
                    />
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={2}>
                <Typography variant="body2" color="textSecondary">Connected Banks</Typography>
                <Typography variant="h6">{openBankingStatus.connected_banks}</Typography>
              </Grid>

              <Grid item xs={12} md={2}>
                <Typography variant="body2" color="textSecondary">Total Accounts</Typography>
                <Typography variant="h6">{openBankingStatus.total_accounts}</Typography>
              </Grid>

              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="textSecondary">Last Sync</Typography>
                <Typography variant="body2">
                  {openBankingStatus.last_sync ? new Date(openBankingStatus.last_sync).toLocaleString('en-GB') : 'Never'}
                </Typography>
              </Grid>

              <Grid item xs={12} md={2}>
                <Chip
                  icon={getStatusIcon(openBankingStatus.sync_status)}
                  label={openBankingStatus.sync_status}
                  color={getStatusColor(openBankingStatus.sync_status) as any}
                  size="small"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Connected Accounts Summary */}
      {bankAccounts.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Connected Accounts ({bankAccounts.length})
            </Typography>
            <Grid container spacing={2}>
              {bankAccounts.slice(0, 4).map((account) => (
                <Grid item xs={12} md={3} key={account.id}>
                  <Card variant="outlined">
                    <CardContent sx={{ p: 2 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Avatar sx={{ width: 24, height: 24 }}>
                          {account.bank_name.charAt(0)}
                        </Avatar>
                        <Typography variant="body2" fontWeight="medium">
                          {account.bank_name}
                        </Typography>
                        <Chip
                          icon={getStatusIcon(account.status)}
                          label={account.status}
                          color={getStatusColor(account.status) as any}
                          size="small"
                        />
                      </Box>
                      <Typography variant="body2" color="textSecondary">
                        {account.account_name}
                      </Typography>
                      <Typography variant="h6" color="primary">
                        {account.balance_formatted}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Banking Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant="fullWidth"
          >
            {bankingTabs.map((tab, index) => (
              <Tab
                key={index}
                icon={tab.icon}
                label={tab.label}
                iconPosition="start"
                sx={{ minHeight: 72 }}
              />
            ))}
          </Tabs>
        </Box>

        {/* Tab Description */}
        <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Typography variant="body2" color="textSecondary">
            {bankingTabs[activeTab].description}
          </Typography>
        </Box>

        {/* Active Tab Content */}
        <CardContent>
          <ActiveTabComponent
            bankAccounts={bankAccounts}
            onRefresh={fetchBankingData}
            loading={loading}
          />
        </CardContent>
      </Card>

      {/* Bank Connection Dialog */}
      <Dialog open={showSetupDialog} onClose={() => setShowSetupDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <OpenBankingIcon />
            Connect Your Bank Account
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Open Banking:</strong> Securely connect your UK bank account using Open Banking standards.
              Your login credentials are never shared with us.
            </Typography>
          </Alert>

          <Typography variant="h6" gutterBottom>
            Select Your Bank
          </Typography>

          <Grid container spacing={2}>
            {ukBanks.map((bank, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  variant="outlined"
                  sx={{
                    cursor: connectingBank === bank.name ? 'wait' : 'pointer',
                    '&:hover': { bgcolor: connectingBank === bank.name ? 'action.disabled' : 'action.hover' },
                    opacity: connectingBank === bank.name ? 0.7 : 1
                  }}
                  onClick={() => connectingBank === null && handleConnectBank(bank.name)}
                >
                  <CardContent sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4" sx={{ mb: 1 }}>
                      {bank.logo}
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {bank.name}
                    </Typography>
                    <Chip label="Available" color="success" size="small" sx={{ mt: 1 }} />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSetupDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BankingIntegration;
